<script setup lang="ts">
import type { CmsMedia } from '~/composables/useCmsMedia';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const { getImageUrl } = useCmsMedia();

const props = defineProps<{
  media: CmsMedia | undefined;
  videoLoopEnabled?: boolean;
}>();

const { media } = toRefs(props);

const isExpanded = ref(false);

const pacsbinHint = computed(() => {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  return {
    label: isTouchDevice ? 'Drag to interact' : 'Scroll to Interact',
    icon: isTouchDevice ? 'fa-hand-point-up' : 'fa-computer-mouse-scrollwheel',
    class: isTouchDevice ? 'animate-swipe shadow-medmastery' : 'animate-scroll',
  };
});

const pacsbinUrl = computed(() => {
  if (!media.value?.source) {
    return '';
  }

  const url = new URL(media.value.source);
  url.searchParams.set('header', 'false');
  url.searchParams.set('overlay', 'false');

  return url.toString();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="question-media animate-gradient rounded-xl overflow-hidden">
      <img
        v-if="media && media.type === 'image'"
        class="w-full h-full cursor-pointer block"
        :src="getImageUrl(media, {width: 600, height: 400, output: 'webp'})"
        :title="media.metaData.title"
        :alt="media.metaData.alt"
        lazy
        @click="isExpanded = true"
      >

      <LessonPlayer
        v-else-if="media && media.source && media.type === 'video'"
        force-disable-autoplay
        :vimeo-id="media.source"
        :loop="videoLoopEnabled"
      />

      <iframe
        v-else-if="media?.type === 'pacsbin'"
        :src="pacsbinUrl"
        frameborder="0"
        class="w-full h-full aspect-[4/3] block"
      />
    </div>

    <div
      class="items-center justify-between"
      :class="media?.type !== 'pacsbin' ? 'lg:flex hidden' : 'flex'"
    >
      <div
        v-if="media?.type === 'pacsbin'"
        class="text-14 flex items-center gap-2"
      >
        <FontAwesomeIcon
          :icon="pacsbinHint.icon"
          :class="pacsbinHint.class"
        />
        <div class="text-nowrap">
          {{pacsbinHint.label}}
        </div>
      </div>

      <div class="w-full" />

      <Button
        class="p-3"
        text
        severity="secondary"
        @click="isExpanded = !isExpanded"
      >
        <FontAwesomeIcon
          icon="fa-magnifying-glass-plus"
          class="text-16"
        />
      </Button>
    </div>

    <Dialog
      v-model:visible="isExpanded"
      :draggable="false"
      modal
      header="Quiz media"
      class="!w-[100vw] !h-[100svh]"
      :pt="{
        content: {
          class: [
            'p-0 !rounded-none',
            'w-full h-full max-w-[100vw] max-h-[calc(100svh-65px)]',
            'flex items-center justify-center'
          ]
        },
        header: {
          class: '!rounded-none'
        }
      }"
    >
      <img
        v-if="media && media.type === 'image'"
        class="w-full h-full object-contain"
        :src="getImageUrl(media, {width: 1920, height: 1080, output: 'webp'})"
        :title="media.metaData.title"
        :alt="media.metaData.alt"
      >

      <LessonPlayer
        v-else-if="media && media.source && media.type === 'video'"
        class="w-full h-full"
        force-disable-autoplay
        :vimeo-id="media.source"
        :loop="videoLoopEnabled"
      />

      <iframe
        v-else-if="media?.type === 'pacsbin'"
        :src="pacsbinUrl"
        class="w-full h-full"
        frameborder="0"
      />
    </Dialog>
  </div>
</template>

<style lang="scss">
.question-media {
  border: 3px solid var(--palette-neutral-20);
  background: linear-gradient(90deg,var(--palette-neutral-10), #ffffff, var(--palette-neutral-10), #ffffff);
  background-size: 300% 100%;
}

.p-dialog-mask.p-component-overlay {
  padding: 0 !important;
}
</style>